import { motion } from "framer-motion";
import { Building2 } from "lucide-react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

/**
 * ActiveRadarEmpty — used on the Matches page when a broker has no matches
 * yet. A calm, brand-styled radar pulse with a central building icon and
 * a reassuring message.
 *
 * Reduced-motion users see the static base (no pulse rings).
 */
export function ActiveRadarEmpty({ testId = "matches-empty-state" }) {
  const reduced = useReducedMotion();

  const ringVariant = (delay) => ({
    initial: { scale: 0.4, opacity: 0.35 },
    animate: {
      scale: [0.4, 1.4],
      opacity: [0.35, 0],
      transition: {
        duration: 2.6,
        repeat: Infinity,
        ease: "easeOut",
        delay,
      },
    },
  });

  return (
    <div
      className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-6 py-16 text-center shadow-mb-card"
      data-testid={testId}
    >
      <div
        className="relative flex h-40 w-40 items-center justify-center"
        aria-hidden="true"
        data-testid="active-radar"
      >
        {!reduced && (
          <>
            <motion.span
              className="absolute inset-0 rounded-full border border-[#35622F]/40"
              {...ringVariant(0)}
            />
            <motion.span
              className="absolute inset-0 rounded-full border border-[#35622F]/40"
              {...ringVariant(0.9)}
            />
            <motion.span
              className="absolute inset-0 rounded-full border border-[#35622F]/40"
              {...ringVariant(1.8)}
            />
          </>
        )}
        {reduced && (
          <span className="absolute inset-4 rounded-full border border-[#35622F]/30" />
        )}
        <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-[#35622F]/10 text-[#35622F] ring-1 ring-[#35622F]/25">
          <Building2 className="h-7 w-7" />
        </span>
      </div>
      <p
        className="mt-6 max-w-md text-sm leading-relaxed text-gray-500"
        data-testid="active-radar-message"
      >
        AI is actively scanning the network for your perfect match...
      </p>
    </div>
  );
}
